import React from "react";
import {
  makeStyles,
  mergeClasses,
  tokens,
  Button,
  Dropdown,
  Option,
  Text,
} from "@fluentui/react-components";
import {
  Add20Regular,
  Dismiss20Regular,
  ReOrder20Regular,
} from "@fluentui/react-icons";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MetricType, METRIC_LABELS } from "../types";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  orderItem: {
    display: "grid",
    gridTemplateColumns: "32px minmax(0, 1fr) 32px",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  orderItemDragging: {
    opacity: 0.5,
  },
  iconButton: {
    width: "32px",
    height: "32px",
    minWidth: "32px",
  },
  dragHandle: {
    cursor: "grab !important",
    touchAction: "none",
  },
  dropdown: {
    minWidth: 0,
    width: "100%",
  },
  dropdownButton: {
    minWidth: 0,
    whiteSpace: "normal",
    textAlign: "left",
    overflowWrap: "anywhere",
  },
  listbox: {
    maxWidth: "calc(100vw - 32px)",
  },
  addButton: {
    alignSelf: "flex-start",
    paddingLeft: "6px",
    paddingRight: "6px",
  },
});

interface SortableMetricRowProps {
  metric: MetricType;
  index: number;
  options: MetricType[];
  open: boolean;
  canRemove: boolean;
  onReplace: (metric: MetricType, replacement: string) => void;
  onRemove: (metric: MetricType) => void;
  onOpenChange: (metric: MetricType, open: boolean) => void;
  registerDropdown: (
    metric: MetricType,
    button: HTMLButtonElement | null,
  ) => void;
}

const SortableMetricRow: React.FC<SortableMetricRowProps> = ({
  metric,
  index,
  options,
  open,
  canRemove,
  onReplace,
  onRemove,
  onOpenChange,
  registerDropdown,
}) => {
  const styles = useStyles();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: metric, disabled: !canRemove });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={mergeClasses(
        styles.orderItem,
        isDragging && styles.orderItemDragging,
      )}
    >
      <Button
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        appearance="transparent"
        icon={<ReOrder20Regular />}
        aria-label={`Reorder ${METRIC_LABELS[metric]}`}
        disabled={!canRemove}
        className={mergeClasses(styles.iconButton, styles.dragHandle)}
      />
      <Dropdown
        aria-label={`Metric ${index + 1}`}
        value={METRIC_LABELS[metric]}
        selectedOptions={[metric]}
        open={open}
        onOpenChange={(_, data) => onOpenChange(metric, data.open)}
        onOptionSelect={(_, data) => {
          if (data.optionValue) onReplace(metric, data.optionValue);
        }}
        className={styles.dropdown}
        button={{
          ref: (button) => registerDropdown(metric, button),
          className: styles.dropdownButton,
        }}
        listbox={{ className: styles.listbox }}
        size="small"
      >
        {options.map((option) => (
          <Option key={option} value={option}>
            {METRIC_LABELS[option]}
          </Option>
        ))}
      </Dropdown>
      <Button
        appearance="transparent"
        icon={<Dismiss20Regular />}
        aria-label={`Remove ${METRIC_LABELS[metric]}`}
        disabled={!canRemove}
        onClick={() => onRemove(metric)}
        className={styles.iconButton}
      />
    </div>
  );
};

interface MetricSelectorProps {
  selectedMetrics: MetricType[];
  onChange: (metrics: MetricType[]) => void;
  maxSelections?: number;
  accessibleMetrics?: MetricType[];
}

const DEFAULT_METRICS: MetricType[] = [
  "revenue",
  "revenueDiffEuro",
  "revenueDiffPercent",
  "revenuePercentTotal",
  "quantity",
  "quantityDiffNumber",
  "quantityDiffPercent",
];

export const MetricSelector: React.FC<MetricSelectorProps> = ({
  selectedMetrics,
  onChange,
  maxSelections = 3,
  accessibleMetrics = DEFAULT_METRICS,
}) => {
  const styles = useStyles();
  const [openMetric, setOpenMetric] = React.useState<MetricType | null>(null);
  const dropdownButtons = React.useRef(
    new Map<MetricType, HTMLButtonElement>(),
  );
  const pendingFocus = React.useRef<MetricType | null>(null);
  const dragging = React.useRef(false);

  React.useLayoutEffect(() => {
    if (pendingFocus.current) {
      dropdownButtons.current.get(pendingFocus.current)?.focus();
      pendingFocus.current = null;
    }
  }, [selectedMetrics]);

  React.useEffect(() => {
    if (openMetric && !selectedMetrics.includes(openMetric))
      setOpenMetric(null);
  }, [openMetric, selectedMetrics]);

  const registerDropdown = React.useCallback(
    (metric: MetricType, button: HTMLButtonElement | null) => {
      if (button) dropdownButtons.current.set(metric, button);
      else dropdownButtons.current.delete(metric);
    },
    [],
  );

  const handleOpenChange = React.useCallback(
    (metric: MetricType, open: boolean) => {
      setOpenMetric((current) =>
        open ? metric : current === metric ? null : current,
      );
    },
    [],
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const availableMetrics = accessibleMetrics.filter(
    (metric) => !selectedMetrics.includes(metric),
  );

  const handleAdd = () => {
    const metric = availableMetrics[0];
    if (!metric || selectedMetrics.length >= maxSelections) return;
    pendingFocus.current = metric;
    setOpenMetric(metric);
    onChange([...selectedMetrics, metric]);
  };

  const handleReplace = (metric: MetricType, replacement: string) => {
    const next = accessibleMetrics.find((option) => option === replacement);
    if (
      !next ||
      next === metric ||
      selectedMetrics.includes(next) ||
      !selectedMetrics.includes(metric)
    )
      return;
    pendingFocus.current = next;
    setOpenMetric(null);
    onChange(
      selectedMetrics.map((selected) =>
        selected === metric ? next : selected,
      ),
    );
  };

  const handleRemove = (metric: MetricType) => {
    const index = selectedMetrics.indexOf(metric);
    if (selectedMetrics.length <= 1 || index < 0) return;
    pendingFocus.current =
      selectedMetrics[index + 1] ?? selectedMetrics[index - 1];
    setOpenMetric(null);
    onChange(selectedMetrics.filter((selected) => selected !== metric));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    dragging.current = false;
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = selectedMetrics.indexOf(active.id as MetricType);
      const newIndex = selectedMetrics.indexOf(over.id as MetricType);
      if (oldIndex >= 0 && newIndex >= 0) {
        onChange(arrayMove(selectedMetrics, oldIndex, newIndex));
      }
    }
  };

  return (
    <div
      className={styles.container}
      onKeyDown={(event) => {
        if (event.key === "Escape" && dragging.current) event.preventDefault();
      }}
    >
      <Text size={200}>
        {selectedMetrics.length} of {maxSelections}
      </Text>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={() => {
          dragging.current = true;
          setOpenMetric(null);
        }}
        onDragCancel={() => {
          dragging.current = false;
        }}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={selectedMetrics}
          strategy={verticalListSortingStrategy}
        >
          <div className={styles.list}>
            {selectedMetrics.map((metric, index) => (
              <SortableMetricRow
                key={metric}
                metric={metric}
                index={index}
                options={accessibleMetrics.filter(
                  (option) =>
                    option === metric || !selectedMetrics.includes(option),
                )}
                open={openMetric === metric}
                canRemove={selectedMetrics.length > 1}
                onReplace={handleReplace}
                onRemove={handleRemove}
                onOpenChange={handleOpenChange}
                registerDropdown={registerDropdown}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {selectedMetrics.length < maxSelections &&
        availableMetrics.length > 0 && (
          <Button
            appearance="subtle"
            size="small"
            className={styles.addButton}
            icon={<Add20Regular />}
            onClick={handleAdd}
          >
            Add metric
          </Button>
        )}
    </div>
  );
};
